/**
 * SendActionDialog Component
 * Feature: 003-c-program-files - Send Action to Device
 * User Story 1: Send Action with Play Button (MVP)
 */

import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Action, SEND_ACTION_DEFAULTS } from '../types/action';
import { CurrentAction, CurrentActionFormData, SendCurrentActionRequest } from '../types/currentAction';
import { currentActionService } from '../services/currentActionService';
import { currentActionToFormData } from '../utils/timestampUtils';
import { useTranslation } from 'react-i18next';
import { useTapGuard } from '../hooks/useTapGuard';
import { RichText } from './RichText';

export interface SendActionDialogProps {
  open: boolean;
  onClose: () => void;
  deviceId: string;
  action: Action;
  editMode?: boolean;
  currentAction?: CurrentAction | null;
  /**
   * Something is on the tablet and this send replaces it (BA-85 m5, P2): the dialog then
   * says so and follows BA-55 (Send and replace, Cancel focused, the tap guard)
   */
  replaces?: ReplacedOnTablet | null;
}

/** What a send would replace on the child's screen: a running sent schedule, or an action */
export type ReplacedOnTablet =
  | { kind: 'schedule'; name: string; n: number; total: number; device: string }
  | { kind: 'action'; name: string; device: string; awaiting: boolean };

export const SendActionDialog: React.FC<SendActionDialogProps> = ({
  open,
  onClose,
  deviceId,
  action,
  editMode = false,
  currentAction = null,
  replaces: liveReplaces = null,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const guard = useTapGuard();
  const cancelRef = useRef<HTMLButtonElement>(null);
  // Form state with default values
  const [timeToStart, setTimeToStart] = useState<number>(5);
  const [stayUntilParentApproves, setStayUntilParentApproves] = useState<boolean>(false);
  const [stayUntilChecked, setStayUntilChecked] = useState<boolean>(false);
  const [stayUntilFixedTime, setStayUntilFixedTime] = useState<boolean>(true);
  const [duration, setDuration] = useState<number>(30);

  // UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Validation errors
  const [timeToStartError, setTimeToStartError] = useState<string>('');
  const [durationError, setDurationError] = useState<string>('');

  // What this send replaces is taken when the dialog opens and frozen while it sends and after
  // it has sent (BA-85 review 332 R1): the listener then delivers the action just sent, which
  // must not turn the dialog into a confirm to replace it
  const sent = !!successMessage;
  const wasOpen = useRef(false);
  const shownReplaces = useRef(liveReplaces);
  if (open && (!wasOpen.current || !(loading || sent))) shownReplaces.current = liveReplaces;
  wasOpen.current = open;
  const replaces = shownReplaces.current;
  const replacing = !!replaces && !editMode;
  useLayoutEffect(() => {
    if (open && replacing) guard.arm();
  }, [open, replacing, guard]);

  // Pre-fill form when editing
  useEffect(() => {
    if (open) {
      if (editMode && currentAction) {
        const formData = currentActionToFormData(currentAction);
        setTimeToStart(formData.timeToStart);
        setStayUntilParentApproves(formData.stayUntilParentApproves);
        setStayUntilChecked(formData.stayUntilChecked);
        setStayUntilFixedTime(formData.stayUntilFixedTime);
        setDuration(formData.duration);
      } else {
        // Reset to action-specific defaults, falling back to global defaults
        const d = action.defaults;
        setTimeToStart(d?.timeToStart ?? SEND_ACTION_DEFAULTS.timeToStart);
        setStayUntilParentApproves(d?.stayUntilParentApproves ?? SEND_ACTION_DEFAULTS.stayUntilParentApproves);
        setStayUntilChecked(d?.stayUntilChecked ?? SEND_ACTION_DEFAULTS.stayUntilChecked);
        setStayUntilFixedTime(d?.stayUntilFixedTime ?? SEND_ACTION_DEFAULTS.stayUntilFixedTime);
        setDuration(d?.duration ?? SEND_ACTION_DEFAULTS.duration);
      }

      // Clear messages and errors
      setErrorMessage('');
      setSuccessMessage('');
      setTimeToStartError('');
      setDurationError('');
    }
  }, [open, editMode, currentAction, action]);

  // Validate timeToStart
  const validateTimeToStart = (value: number): string => {
    if (value < 0) {
      return t('sendAction.mustBeNonNegative');
    }
    return '';
  };

  // Validate duration
  const validateDuration = (value: number): string => {
    if (stayUntilFixedTime) {
      if (value <= 0) {
        return t('sendAction.mustBePositive');
      }
      if (value > 1440) {
        return t('sendAction.maxDuration');
      }
    }
    return '';
  };

  // Handle timeToStart change
  const handleTimeToStartChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    setTimeToStart(isNaN(value) ? 0 : value);
    setTimeToStartError(validateTimeToStart(isNaN(value) ? 0 : value));
  };

  // Handle duration change
  const handleDurationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    setDuration(isNaN(value) ? 0 : value);
    setDurationError(validateDuration(isNaN(value) ? 0 : value));
  };

  // Handle stayUntilFixedTime checkbox change
  const handleStayUntilFixedTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setStayUntilFixedTime(checked);

    // Clear duration error when unchecked
    if (!checked) {
      setDurationError('');
    } else {
      // Re-validate when checked
      setDurationError(validateDuration(duration));
    }
  };

  // Check if form is valid
  const isFormValid =
    timeToStartError === '' &&
    durationError === '' &&
    (stayUntilFixedTime ? duration > 0 : true);

  // Handle submit
  const handleSubmit = async () => {
    // Final validation
    const timeError = validateTimeToStart(timeToStart);
    const durError = validateDuration(duration);

    setTimeToStartError(timeError);
    setDurationError(durError);

    if (timeError || durError) {
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const request: SendCurrentActionRequest = {
        actionId: action.id,
        timeToStart,
        stayUntilParentApproves,
        stayUntilChecked,
        duration: stayUntilFixedTime ? duration : 0,
      };

      if (editMode) {
        await currentActionService.editAction(deviceId, request);
        setSuccessMessage(t('sendAction.actionUpdatedSuccess'));
      } else {
        await currentActionService.sendAction(deviceId, request);
        setSuccessMessage(t('sendAction.actionSentSuccess'));
      }

      // Auto-close after 1 second
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Failed to send action:', error);
      setErrorMessage(t('sendAction.failedToSave'));
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' && guard.guarding()) return;
        handleCancel();
      }}
      maxWidth="sm"
      fullWidth
      slotProps={replacing ? { transition: { onEntering: () => cancelRef.current?.focus() } } : undefined}
    >
      <DialogTitle>
        {editMode ? t('sendAction.editActionSettings') : action.name}
      </DialogTitle>

      <DialogContent>
        {/* Action description (for create mode) */}
        {!editMode && action.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {action.description}
          </Typography>
        )}

        {/* Success message */}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {/* Error message */}
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {/* Time to start field */}
        <TextField
          label={t('sendAction.timeToStart')}
          type="number"
          value={timeToStart}
          onChange={handleTimeToStartChange}
          error={!!timeToStartError}
          helperText={timeToStartError || t('sendAction.timeToStartHelper')}
          fullWidth
          margin="normal"
          disabled={loading}
          inputProps={{ min: 0 }}
        />

        {/* Stay conditions */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('sendAction.actionCompletesWhen')}
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={stayUntilParentApproves}
                onChange={(e) => setStayUntilParentApproves(e.target.checked)}
                disabled={loading}
              />
            }
            label={t('sendAction.stayUntilParentApproves')}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={stayUntilChecked}
                onChange={(e) => setStayUntilChecked(e.target.checked)}
                disabled={loading}
              />
            }
            label={t('sendAction.stayUntilChecked')}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={stayUntilFixedTime}
                onChange={handleStayUntilFixedTimeChange}
                disabled={loading}
              />
            }
            label={t('sendAction.stayUntilFixedTime')}
          />

          {/* Duration field (enabled only when stayUntilFixedTime is checked) */}
          {stayUntilFixedTime && (
            <TextField
              label={t('sendAction.durationMinutes')}
              type="number"
              value={duration}
              onChange={handleDurationChange}
              error={!!durationError}
              helperText={durationError || t('sendAction.durationHelper')}
              fullWidth
              margin="normal"
              disabled={loading}
              inputProps={{ min: 1, max: 1440 }}
              // Indented under its checkbox, so it is narrower by the indent: fullWidth alone
              // would push it 32 px past the dialog content's right edge (BA-104)
              sx={{ ml: 4, width: (theme) => `calc(100% - ${theme.spacing(4)})` }}
            />
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t('sendAction.anyConditionMet')}
          </Typography>
        </Box>
      </DialogContent>

      {replacing && replaces && (
        <Alert severity="warning" sx={{ mx: 3, mb: 1 }}>
          {replaces.kind === 'schedule' ? (
            <RichText i18nKey="sentSchedule.actionReplaces" values={replaces} />
          ) : (
            <RichText
              i18nKey={replaces.awaiting ? 'sentSchedule.replace.actionAwaiting' : 'sentSchedule.replace.action'}
              values={{ old: replaces.name, device: replaces.device }}
            />
          )}
        </Alert>
      )}

      {replacing ? (
        <DialogActions
          disableSpacing
          sx={isPhone
            ? { flexDirection: 'column', alignItems: 'stretch', gap: '12px', p: '8px 24px 24px' }
            : { gap: 1, px: 3, pb: 2 }}
        >
          <Button
            variant="outlined"
            color="error"
            disabled={!isFormValid || loading || sent}
            onPointerDown={guard.onPointerDown}
            onClick={(event) => { if (guard.accept(event)) handleSubmit(); }}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{
              textTransform: 'none',
              ...(isPhone ? { minHeight: 48, alignSelf: 'flex-start' } : { minHeight: 40 }),
            }}
          >
            {loading ? t('sendAction.sending') : t('sentSchedule.replace.confirm')}
          </Button>
          <Button
            ref={cancelRef}
            variant="contained"
            autoFocus
            onClick={handleCancel}
            disabled={loading}
            sx={{ textTransform: 'none', ...(isPhone ? { minHeight: 48 } : { minHeight: 40, minWidth: 96 }) }}
          >
            {t('common.cancel')}
          </Button>
        </DialogActions>
      ) : (
        <DialogActions>
          <Button onClick={handleCancel} disabled={loading} sx={isPhone ? { minHeight: 48 } : undefined}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!isFormValid || loading || sent}
            sx={isPhone ? { minHeight: 48 } : undefined}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {editMode ? t('sendAction.updating') : t('sendAction.sending')}
              </>
            ) : editMode ? (
              t('sendAction.update')
            ) : (
              t('sendAction.send')
            )}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
